<template>
  <tr>
    <td>
      {{ name }}
      <span v-if="cloud.status < PRINT_CLOUD_STATUS_FINISHED"
        class="cursor--pointer text-primary"
        @click="showEditRefinementModal">
        <i class="fa fa-pencil ml-1"></i>
      </span>
    </td>
    <td>
      <span class="text-primary cursor--pointer" @click="show">
        <i class="fa fa-info-circle"></i> {{ articleName }}
      </span>
      <span v-if="cloud.status < PRINT_CLOUD_STATUS_FINISHED"
        class="cursor--pointer text-primary"
        @click="showEditPrintArticleModal">
        <i class="fa fa-pencil ml-1"></i>
      </span>
    </td>
    <td class="text-right">{{ $filters.formatMoney(price) }}</td>
    <td v-if="cloud.status < PRINT_CLOUD_STATUS_FINISHED">
      <span v-if="configuration.is_logo_print">
        <span v-if="configuration.upload && configuration.upload.file_exists"
          class="text-primary cursor--pointer"
          @click="download">
          <i class="fa fa-download"></i> {{ configuration.upload.original_name }}
        </span>
        <span v-if="configuration.upload && !configuration.upload.file_exists">
          <span class="text-primary">{{ configuration.upload.original_name }}</span><br>
          <span class="mr-2 bg-danger">
            <i class="fa fa-warning text-danger"></i> {{ $t('configurator.labels.file_missing_reupload') }}
          </span>
          <span v-if="cloudStore.isOpen"
            class="text-primary cursor--pointer"
            @click="showUploadModal">
            <i class="fa fa-upload mr-1"></i> {{ $t('configurator.buttons.upload_now') }}
          </span>
        </span>
        <span v-if="!configuration.upload">
          <span class="mr-2 bg-warning">
            <i class="fa fa-warning text-warning"></i> {{ $t('configurator.labels.upload_missing') }}
          </span>
          <span v-if="cloudStore.isOpen"
            class="text-primary cursor--pointer"
            @click="showUploadModal">
            <i class="fa fa-upload mr-1"></i> {{ $t('configurator.buttons.upload_now') }}
          </span>
        </span>
      </span>
      <span v-else>
        {{ configuration.label }}
      </span>
    </td>
    <td v-else>
      <span v-if="configuration.is_logo_print">
        <span v-if="configuration.upload" class="text-primary">
          {{ configuration.upload.original_name }} ({{ $t('configurator.labels.deleted') }})
        </span>
      </span>
      <span v-else>
        {{ configuration.label }}
      </span>
    </td>
    <td>
      <span v-if="configuration.upload && cloudStore.isOpen"
        class="text-primary cursor--pointer"
        @click="confirmDeleteUpload">
        <i class="fa fa-trash mr-1"></i> {{ $t('configurator.buttons.delete_graphic_file') }}
      </span>

      <span v-if="!configuration.is_logo_print && cloudStore.isOpen"
        class="text-primary cursor--pointer"
        @click="showEditModal">
        <i class="fa fa-pencil mr-1"></i> {{ $t('configurator.buttons.edit_lettering') }}
      </span>
    </td>
  </tr>
  <modal :is-active="uploadModalActive" :title="$t('configurator.modals.titles.upload_graphic_file')">
    <template #body>
      <div class="form-group">
        <label for="property-images">{{ $t('configurator.form.labels.select_file') }}:</label>
        <drop-zone :id="dropzoneId" @drop.prevent="drop" @change="selectedFile"></drop-zone>
        <p class="help-block mb-1 pb-1" style="font-weight: 400;">
          {{ $t('configurator.form.labels.selected_file') }}: <span v-if="dropzoneFile">{{ dropzoneFile.name }}</span>
        </p>
        <printing-information></printing-information>
      </div>
      <div class="form-group">
        <div class="row">
          <div class="col-md-12 text-center">
            <button class="btn btn-warning mr-2" @click="cancelUpload">
              {{ $t('configurator.buttons.cancel') }}
            </button>
            <button class="btn btn-primary" @click="upload">
              {{ $t('configurator.buttons.upload_graphic_file') }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </modal>
  <EditBoothRefinementLabelModal v-if="editLabelModalActive"
    :form="editLabelForm"
    :refinement="refinement"
    @update="updateLabel"
    @cancel="hideEditModal"/>
  <EditBoothRefinementModal
    :is-active="editRefinementModalActive"
    :refinement="refinement"
    @hide="hideEditRefinementModal" />
  <EditBoothRefinementPrintArticleModal v-if="editPrintArticleModalActive"
    :refinement="refinement"
    @hide="hideEditPrintArticleModal" />
</template>

<script setup>
import { computed, ref, reactive, inject } from 'vue'
import { useMainStore } from '@/store'
import { useCloudStore } from '@/stores/cloud'
import { PRINT_CLOUD_STATUS_FINISHED, PRINT_CLOUD_STATUS_OPEN } from '@/constants'
import FormDataForm from '@/classes/FormDataForm'
import Swal from 'sweetalert2'
import axios from 'axios'
import { showErrorToast, showLoadingIndicator, showSuccessToast, showWarningToast } from '@/utils/toast'
import { useI18n } from 'vue-i18n'
import Logger from '@/services/logging'
import { addLateDeliverySurcharge } from '@/utils/helper'
import Form from '@/classes/Form'
import EditBoothRefinementLabelModal from '@/components_new/PrintCloud/EditBoothRefinementLabelModal.vue'
import EditBoothRefinementModal from '@/components_new/PrintCloud/EditBoothRefinementModal.vue'
import EditBoothRefinementPrintArticleModal from '@/components_new/PrintCloud/EditBoothRefinementPrintArticleModal.vue'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
const cloudStore = useCloudStore()
/** @type {Logger} */
const logger = inject('logger', null)
// define props
const props = defineProps({
  refinement: {
    type: Object,
    required: true
  }
})
// define emits
const emit = defineEmits(['download', 'show'])
// define reactive props
const dropzoneFile = ref('')
const uploadModalActive = ref(false)
const uploadForm = reactive(new FormDataForm({
  file: null,
  configuration_id: null,
  use_graphic_for_all_prints: false
}))
const editLabelModalActive = ref(false)
const editLabelForm = reactive(new Form({
  label: ''
}))
const editRefinementModalActive = ref(false)
const editPrintArticleModalActive = ref(false)
// define computed props
const inEnglish = computed(() => store.inEnglish)
const cloud = computed(() => store.tmp.cloud)
const earlyBirdMode = computed(() => store.tmp.early_bird_mode)
const lateDelivery = computed(() => store.tmp.late_delivery)

const hasRefinement = computed(() => {
  if (props.refinement === undefined || props.refinement === null) {
    logger.debug(
      'refinement undefined',
      JSON.stringify(props),
      JSON.stringify(cloud.value)
    )

    return false
  }

  return true
})

const name = computed(() => {
  if (!hasRefinement.value) {

    return ''
  }

  if (props.refinement.booth_refinement === null) {
		logger.debug(
      'name - booth refinement null',
      JSON.stringify(props.refinement),
      JSON.stringify(cloud.value)
    )

    return ''
  }

  return (inEnglish.value)
    ? props.refinement.booth_refinement.name_en
    : props.refinement.booth_refinement.name
})

const articleName = computed(() => {
  if (!hasRefinement.value) {
    return ''
  }

  return (inEnglish.value)
    ? props.refinement.article.name_en
    : props.refinement.article.name
})

const configuration = computed(() => {
  if (!hasRefinement.value) {
    return {}
  }

  return props.refinement.configurations[0]
})

const dropzoneId = computed(() => {
  return 'dropzone_booth_' + configuration.value.id
})

const price = computed(() => {
  if (!hasRefinement.value) {
    return 0
  }

  return (earlyBirdMode.value)
    ? addLateDeliverySurcharge(props.refinement.exhibition_prices.price_net, lateDelivery.value)
    : props.refinement.exhibition_prices.price_net
})
// define methods
const show = () => {
  emit('show', props.refinement.article)
}

const download = () => {
  emit('download', configuration.value.upload)
}

const confirmDeleteUpload = () => {
  Swal.fire({
    title: t('configurator.notices.titles.delete_upload'),
    text: t('configurator.notices.messages.delete_upload'),
    showCancelButton: true,
    confirmButtonText: t('configurator.buttons.yes'),
    cancelButtonText: t('configurator.buttons.no'),
  })
    .then((result) => {
      if (result.isConfirmed) {
        deleteUpload()
      }
    })
}

const deleteUpload = async () => {
  showLoadingIndicator(t('print_cloud.indicator.delete_upload'))
  await axios.delete('/print/' + cloud.value.uuid + '/upload/' + configuration.value.upload.id)
    .then(response => {
      cloudStore.data.print_articles = response.data.articles
      store.tmp.booth = response.data.booth
      showSuccessToast(t('configurator.toasts.messages.upload_deleted'), t('toast.title.success'))
    })
    .catch(err => {
      console.error(err)
      showErrorToast(t('toast.message.error'), t('toast.title.error'))
    })
}

const showUploadModal = (slot) => {
  uploadForm.slot = slot
  uploadModalActive.value = true
}

const hideUploadModal = () => {
  uploadModalActive.value = false
}

const showEditModal = () => {
  editLabelForm.label = configuration.value.label ?? ''
  editLabelModalActive.value = true
}

const hideEditModal = () => {
  editLabelModalActive.value = false
}

const updateLabel = async () => {
  showLoadingIndicator(t('configurator.indicators.update_lettering'), t('configurator.indicators.one_moment_please'))
  await cloudStore.updateConfigurationLabel(editLabelForm, configuration.value.id)
    .then(() => {
      showSuccessToast(t('configurator.toasts.messages.lettering_updated'), t('toast.title.success'))
      hideEditModal()
    })
    .catch(() => showErrorToast(t('configurator.toasts.errors.lettering_updated'), t('toast.title.error')))
}

const drop = (e) => {
  dropzoneFile.value = e.dataTransfer.files[0]
}

const selectedFile = () => {
  dropzoneFile.value = document.getElementById(dropzoneId.value).files[0]
}

const upload = async () => {
  showLoadingIndicator(t('configurator.indicators.upload'))
  uploadForm.file = dropzoneFile.value
  uploadForm.configuration_id = configuration.value.id
  await uploadForm.post('/print/' + cloud.value.uuid + '/upload')
    .then((response) => {
      cloudStore.data.print_articles = response.data.articles
      store.tmp.booth = response.data.booth
      uploadForm.resetToOriginal()
      dropzoneFile.value = ''
      hideUploadModal()
      showSuccessToast(t('configurator.toasts.messages.upload_successful'), t('toast.title.success'))
    })
    .catch(err => {
      if (err.status === 413) {
        showWarningToast(t('toast.warnings.too_large'), t('toast.title.warning'), 5000)
      } else {
        showErrorToast(t('toast.message.error'), t('toast.title.error'))
      }
    })
}

const cancelUpload = async () => {
  dropzoneFile.value = ''
  hideUploadModal()
}

const showEditRefinementModal = () => {
  editRefinementModalActive.value = true
}

const hideEditRefinementModal = () => {
  editRefinementModalActive.value = false
}

const showEditPrintArticleModal = () => {
  editPrintArticleModalActive.value = true
}

const hideEditPrintArticleModal = () => {
  editPrintArticleModalActive.value = false
}
</script>